import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { listMembers } from '~/features/organization/services/member.service';
import type { TenantMember } from '~/features/organization/types/member.types';
import { useResolvedSubsidiaryScope } from '~/features/platform/hooks/use-subsidiary-scope';
import { ActorResolverContext, buildActorResolver } from '~/hooks/use-actor-resolver';

// Mirrors the backend directory's own ceilings so a huge tenant can't stall a
// page: 100 per request, 20 requests max. A tenant past that resolves the first
// 2000 members and the rest keep their short id.
const PAGE_SIZE = 100;
const MAX_PAGES = 20;

// Members change far less often than the pages this renders on. 15 minutes
// matches the backend directory's own Redis TTL, so the two layers can't
// disagree for long.
const STALE_TIME_MS = 15 * 60 * 1000;

/**
 * Page through the subsidiary's members and flatten them into one list.
 * Sequential rather than parallel: page one is what reports `totalPages`, so
 * the work isn't knowable up front, and this runs once per subsidiary per 15
 * minutes.
 */
async function fetchAllMembers(subsidiaryId: string): Promise<TenantMember[]> {
  const first = await listMembers(subsidiaryId, 1, PAGE_SIZE);
  const totalPages = Math.min(first.meta.totalPages || 1, MAX_PAGES);
  const members = [...first.data];

  for (let page = 2; page <= totalPages; page++) {
    const next = await listMembers(subsidiaryId, page, PAGE_SIZE);
    members.push(...next.data);
  }

  return members;
}

/**
 * STOPGAP. Fetches the scoped subsidiary's members once and supplies the actor
 * resolver, so `ActorLabel` can name actors the API returned as unresolved
 * short ids. See `~/hooks/use-actor-resolver` for why this exists and what it
 * cannot fix.
 *
 * Mounted in the tenant layout so every actor surface resolves or degrades
 * identically — a mix of real names on one page and short ids on another is
 * the specific outcome the backend's own render ladder is written to avoid.
 */
export function ActorResolverProvider({ children }: { children: ReactNode }) {
  // The same hook the subsidiary-scoped layout feeds its context from, so the
  // members fetched are always for the subsidiary the page is scoped to,
  // including a `?sc=` override by an operator.
  const { id: subsidiaryId } = useResolvedSubsidiaryScope();

  // No retry and no toast: this is a cosmetic upgrade to a field that already
  // holds a correct — if terse — value, so a failure must degrade to the short
  // id rather than surface as an error the user cannot act on. The rejection
  // still lands in the query's own error state rather than being swallowed.
  const { data: members } = useQuery({
    queryKey: queryKeys.members.directory(subsidiaryId),
    queryFn: () => fetchAllMembers(subsidiaryId),
    enabled: !!subsidiaryId,
    staleTime: STALE_TIME_MS,
    retry: false,
  });

  const resolve = useMemo(() => {
    const byId = new Map<string, TenantMember>();
    for (const member of members ?? []) byId.set(member.id, member);
    return buildActorResolver(byId);
  }, [members]);

  return <ActorResolverContext.Provider value={resolve}>{children}</ActorResolverContext.Provider>;
}
